import { type JSX, useMemo, useState } from 'react';
import Box from 'carbon-react/esm/components/box';
import '../../../../App.css';
import {
  FlatTable,
  FlatTableHead,
  FlatTableBody,
  FlatTableRow,
  FlatTableHeader,
  FlatTableCell,
} from 'carbon-react/esm/components/flat-table';
import { useParams } from 'react-router-dom';
import { useAccessToken } from '../../../../hooks/useAccessToken';
import { useLogger } from '../../../../context/useLogger';
import AcceptedStatusButton from '../acceptedStatusButton';
import RefusedStatusButton from '../refusedStatusButton';
import PdfFileButton from '../PdfFileButton';
import ZipFileButton from '../ZipFileButton';
import OrangeCheckButton from '../orangeCheck';
import OrangeTriangleButton from '../orangeTriangle';
import { useNotification } from '../../../../utils/notificationHelpers';
import { NotificationMessage } from '../../../../components/NotificationMessage';
import { DsnBilan } from '../../../../types/bilan.types';
import TablePager from '../../../../pages/dsn-list/component/TablePager';
import { handleZipBilanDownload } from './bilanHandlers';
import { formatDate } from '../../../../utils/dateFormatters';
import { EdiDsnBilansFileService } from '../../../../services/EdiDsnBilansFileService';
import { useQueryBilans } from '../../../../hooks/useQueryBilans';
import PreviewListComponent from '../../../../components/PreviewList';
import Typography from 'carbon-react/esm/components/typography';
import { parseQueryBilansResponse } from '../../../../utils/odataResponseHelper';

export default function BilansTab() {
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const { instanceId: urlInstanceId } = useParams<{ instanceId: string }>();
  const getAccessToken = useAccessToken();
  const logger = useLogger();

  const {
    showNotification,
    notificationMessage,
    notificationVariant,
    displayNotification,
    hideNotification,
  } = useNotification();

  const queryResult = useQueryBilans(urlInstanceId, currentPage, pageSize);
  const bilansResponse = parseQueryBilansResponse(queryResult);
  const bilans = bilansResponse.status === 'success' ? bilansResponse.data.items : [];
  const totalCount = bilansResponse.status === 'success' ? bilansResponse.data.totalCount : 0;

  const ediBilanFileService = useMemo(
    () => new EdiDsnBilansFileService(getAccessToken, logger),
    [getAccessToken, logger],
  );

  const displayToast = (message: string, type: 'error' | 'success') => {
    displayNotification(message, type);
  };

  const handleZipClick = (bilan: DsnBilan) => {
    void handleZipBilanDownload(bilan, ediBilanFileService, logger, displayToast);
  };

  const bilanDetailPath = (bilan: DsnBilan) =>
    `/dsn-list/${urlInstanceId}/bilans/${bilan.InstanceId}`;

  // Même destination et même comportement (nouvel onglet indépendant) que le clic sur
  // le Libellé HTML/PDF de la ligne : cf. le lien <a target="_blank"> plus bas. Un
  // Button carbon-react est un <button> natif, il ne peut pas porter de href — donc
  // pas de clic droit "ouvrir dans un nouvel onglet" natif possible ici, contrairement au
  // Libellé.
  const openBilanInNewTab = (bilan: DsnBilan) => {
    window.open(bilanDetailPath(bilan), '_blank', 'noopener,noreferrer');
  };

  const handlePagination = (nextPage: number, newPageSize?: number) => {
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }
    setCurrentPage(nextPage);
  };

  if (queryResult.isLoading) {
    return <PreviewListComponent />;
  }

  const content: JSX.Element = (
    <div className="bilans-table-wrapper edi-list-table">
      <FlatTable
        colorTheme="light"
        hasOuterVerticalBorders={false}
        bottomBorderRadius="borderRadius000"
        marginBottom={1}
        isZebra
      >
        <FlatTableHead>
          <FlatTableRow horizontalBorderSize="medium">
            <FlatTableHeader px="2">Statut</FlatTableHeader>
            <FlatTableHeader px="2">Date du bilan</FlatTableHeader>
            <FlatTableHeader px="2">Libellé</FlatTableHeader>
            <FlatTableHeader px="2">Organisme</FlatTableHeader>
          </FlatTableRow>
        </FlatTableHead>
        <FlatTableBody>
          {bilans.length === 0 ? (
            <FlatTableRow>
              <FlatTableCell colspan={4} px="2">
                <Typography>Aucun bilans disponible pour cette déclaration.</Typography>
              </FlatTableCell>
            </FlatTableRow>
          ) : (
            bilans.map(bilan => {
              let statusButton: JSX.Element | null = null;

              if (bilan.IsPdfFile === true) {
                statusButton = (
                  <PdfFileButton
                    onClick={e => {
                      e.stopPropagation();
                      openBilanInNewTab(bilan);
                    }}
                  />
                );
              } else if (bilan.IsZipFile === true) {
                statusButton = (
                  <ZipFileButton
                    onClick={e => {
                      e.stopPropagation();
                      handleZipClick(bilan);
                    }}
                  />
                );
              } else if (bilan.Statut === 'KO') {
                statusButton = (
                  <RefusedStatusButton
                    onClick={e => {
                      e.stopPropagation();
                      openBilanInNewTab(bilan);
                    }}
                  />
                );
              } else if (bilan.Statut === 'ANO') {
                statusButton = (
                  <OrangeTriangleButton
                    onClick={e => {
                      e.stopPropagation();
                      openBilanInNewTab(bilan);
                    }}
                  />
                );
              } else if (bilan.Statut === 'OK') {
                statusButton = (
                  <AcceptedStatusButton
                    onClick={e => {
                      e.stopPropagation();
                      openBilanInNewTab(bilan);
                    }}
                  />
                );
                if (bilan.StatutClass === 'OrangeCheck') {
                  statusButton = (
                    <OrangeCheckButton
                      onClick={e => {
                        e.stopPropagation();
                        openBilanInNewTab(bilan);
                      }}
                    />
                  );
                }
              }
              return (
                <FlatTableRow
                  key={bilan.InstanceId}
                  horizontalBorderColor="--colorsUtilityMajor050"
                  highlighted={false}
                >
                  <FlatTableCell px="2">{statusButton}</FlatTableCell>
                  <FlatTableCell px="2">{formatDate(bilan.LastModificationStamp)}</FlatTableCell>
                  <FlatTableCell px="2">
                    {bilan.IsZipFile ? (
                      <span
                        role="link"
                        tabIndex={0}
                        className="table-link"
                        onClick={e => {
                          e.stopPropagation();
                          handleZipClick(bilan);
                        }}
                        onKeyDown={e => {
                          const isEnterKey = e.key === 'Enter';
                          const isSpaceKey =
                            e.code === 'Space' ||
                            e.key === ' ' ||
                            e.key === 'Space' ||
                            e.key === 'Spacebar';

                          if (isEnterKey || isSpaceKey) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleZipClick(bilan);
                          }
                        }}
                      >
                        {bilan.DsnTypeBilanObj?.Libelle || '-'}
                      </span>
                    ) : (
                      // Le bilan est un document externe à la plateforme : il s'ouvre toujours
                      // dans un nouvel onglet indépendant (target="_blank"), quel que soit le
                      // type de clic. On n'a donc pas besoin d'intercepter le clic gauche — le
                      // seul cas particulier est la touche Espace, qui n'active pas nativement
                      // une ancre (contrairement à Entrée).
                      <a
                        href={bilanDetailPath(bilan)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="table-link"
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => {
                          e.stopPropagation();
                          const isSpaceKey =
                            e.code === 'Space' ||
                            e.key === ' ' ||
                            e.key === 'Space' ||
                            e.key === 'Spacebar';

                          if (isSpaceKey) {
                            e.preventDefault();
                            openBilanInNewTab(bilan);
                          }
                        }}
                      >
                        {bilan.DsnTypeBilanObj?.Libelle || '-'}
                      </a>
                    )}
                  </FlatTableCell>
                  <FlatTableCell px="2">{bilan.Organisme || '-'}</FlatTableCell>
                </FlatTableRow>
              );
            })
          )}
        </FlatTableBody>
      </FlatTable>
      <TablePager
        totalRecords={totalCount}
        pageSize={pageSize}
        currentPage={currentPage}
        onPagination={(next, size) => handlePagination(next, size)}
        pageSizeSelectionOptions={[
          { id: '5', name: 5 },
          { id: '10', name: 10 },
          { id: '20', name: 20 },
          { id: '50', name: 50 },
        ]}
      />
    </div>
  );

  return (
    <Box height="100%" flex="1" borderRadius="borderRadius000" maxHeight="600px">
      <NotificationMessage
        open={showNotification}
        onDismiss={hideNotification}
        variant={notificationVariant}
      >
        {notificationMessage}
      </NotificationMessage>
      {content}
    </Box>
  );
}
